import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import ArrowForwardIosRoundedIcon from "@mui/icons-material/ArrowForwardIosRounded";
import { Box, Grid, IconButton } from "@mui/material";
import { useEffect, useMemo, useRef, useState } from "react";
import useAPI from "../../../../../api/hooks/useAPI";
import useColors from "../../../../../api/hooks/useColors";
import LoadingIcon from "../../../../../components/LoadingIcon";
import SecurityDomainBox from "./SecurityDomainBox";

export default function SecurityDomainScoreChart({
  domainScores,
  height,
  shouldBeLoaded,
  link = "",
  showTitles = true,
  sx = {},
  trendData = {},
}) {
  const colors = useColors();
  const [loading, setLoading] = useState(true);
  const scrollContainer = useRef(null);
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);
  const { trackEvent } = useAPI();
  // Sort domains by coverage and generate chart options
  const { sortedDomains, chartOptions } = useMemo(() => {
    if (Object.keys(domainScores).length === 0) {
      return { sortedDomains: [], chartOptions: {} };
    }

    // Sort domains by coverage (lowest first)
    const sorted = Object.entries(domainScores)
      .map(([domain, data]) => ({ domain, ...data }))
      .sort((a, b) => a.coverage - b.coverage);

    // Generate chart options for each domain
    const options = {};
    sorted.forEach(({ domain, coverage }) => {
      options[domain] = {
        series: [coverage],
        colors: [colors.primary_300main],
        legend: { show: false },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${val}%`,
          style: {
            fontSize: "12px",
            fontFamily: "Poppins",
            fontWeight: "400",
          },
        },
        stroke: { lineCap: "round" },
        fill: { type: "solid" },
        plotOptions: {
          radialBar: {
            startAngle: -90,
            endAngle: 90,
            dataLabels: {
              name: { show: false },
              value: {
                offsetY: 0,
                fontSize: "18px",
                fontFamily: "Poppins",
                fontWeight: "600",
              },
            },
          },
        },
      };
    });

    return { sortedDomains: sorted, chartOptions: options };
  }, [domainScores, colors.primary_300main]);

  // Handle loading state and arrow visibility
  useEffect(() => {
    if (shouldBeLoaded || sortedDomains.length > 0) {
      setLoading(false);
    }
  }, [shouldBeLoaded, sortedDomains.length]);

  useEffect(() => {
    const container = scrollContainer.current;

    const updateArrowVisibility = () => {
      if (!container) return;

      const hasOverflow = container.scrollWidth > container.offsetWidth;
      if (!hasOverflow) {
        setShowLeftArrow(false);
        setShowRightArrow(false);
      } else {
        setShowLeftArrow(container.scrollLeft > 0);
        setShowRightArrow(
          container.scrollLeft <
            container.scrollWidth - container.offsetWidth - 10
        );
      }
    };

    if (container && !loading) {
      const timeoutId = setTimeout(updateArrowVisibility, 100);
      container.addEventListener("scroll", updateArrowVisibility);
      window.addEventListener("resize", updateArrowVisibility);

      return () => {
        clearTimeout(timeoutId);
        container.removeEventListener("scroll", updateArrowVisibility);
        window.removeEventListener("resize", updateArrowVisibility);
      };
    }
  }, [loading, sortedDomains.length]);

  const scroll = (scrollOffset) => {
    const container = scrollContainer.current;
    if (!container) return;

    container.scrollLeft += scrollOffset;
  };

  return (
    <>
      {/* Chart part */}
      {loading ? (
        <Grid item>
          <Grid
            container
            height="240px"
            justifyContent="center"
            alignItems="center"
          >
            <Grid item>
              <LoadingIcon />
            </Grid>
          </Grid>
        </Grid>
      ) : (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "100%",
            height: "140px",
            gap: 1,
          }}
        >
          {/* Left Arrow - positioned outside chart area */}
          {showLeftArrow ? (
            <IconButton
              onClick={() => {
                trackEvent(
                  "User clicked on left arrow in Security Domain Score"
                );
                scroll(-180);
              }}
              sx={{
                flexShrink: 0,
                width: "32px",
                height: "32px",
              }}
            >
              <ArrowBackIosNewRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: "32px", flexShrink: 0 }} /> // Placeholder to maintain spacing
          )}

          {/* Chart Container */}
          <Box
            ref={scrollContainer}
            className="scroll-container"
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              justifyContent: "flex-start",
              alignItems: "center",
              overflowX: "scroll",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "-ms-overflow-style": "none",
              scrollbarWidth: "none",
            }}
          >
            {sortedDomains.map(({ domain, coverage, pillarsCount }) => (
              <Box
                key={domain}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                <SecurityDomainBox
                  domain={domain}
                  coverage={coverage}
                  pillarsCount={pillarsCount}
                  chartOptions={chartOptions[domain]}
                  trendData={trendData[domain]}
                />
              </Box>
            ))}
          </Box>

          {/* Right Arrow - positioned outside chart area */}
          {showRightArrow ? (
            <IconButton
              onClick={() => {
                trackEvent(
                  "User clicked on right arrow in Security Domain Score"
                );
                scroll(180);
              }}
              sx={{
                flexShrink: 0,
                width: "32px",
                height: "32px",
              }}
            >
              <ArrowForwardIosRoundedIcon
                sx={{ width: "16px", height: "16px" }}
              />
            </IconButton>
          ) : (
            <Box sx={{ width: "32px", flexShrink: 0 }} /> // Placeholder to maintain spacing
          )}
        </Box>
      )}
    </>
  );
}
